import { t as getEnvironment, u as Environment } from "./Environment-B1ZpdxIS.js";
import { t as loggerUtils } from "./Logging-BxRsYlKX.js";
import Cookies from "universal-cookie";
import * as Sentry from "@sentry/react";
import { jsx } from "react/jsx-runtime";
import { asyncWithLDProvider, defaultReactOptions, useLDClient } from "launchdarkly-react-client-sdk";
//#region src/FeatureFlags/internal/environment.ts
function getLaunchDarklyClientId(env) {
	switch (env) {
		case Environment.STAGING:
		case Environment.TESTING:
		case Environment.EPHEMERAL: return process.env.REACT_APP_LAUNCHDARKLY_CLIENTID_STAGING || "missing-REACT_APP_LAUNCHDARKLY_CLIENTID_STAGING-see-code-comment";
		case Environment.PRODUCTION: return process.env.REACT_APP_LAUNCHDARKLY_CLIENTID_PRODUCTION || "missing-REACT_APP_LAUNCHDARKLY_CLIENTID_PRODUCTION-see-code-comment";
		case Environment.LOCAL: return "fake-client-id-for-Environment.LOCAL-see-code-comment";
		case Environment.UNKNOWN:
		default: return "fake-client-id-for-Environment.UNKNOWN-see-code-comment";
	}
}
//#endregion
//#region src/FeatureFlags/internal/FakeLaunchDarkly/Client.ts
/**
* FakeLDClient is only for preventing networking connections from being made in
* local development. For testing, use the {@link jest-launchdarkly-mock} package.
*/
var FakeLaunchDarklyClient = class {
	flags = {};
	context;
	constructor(flags, context) {
		this.flags = flags;
		this.context = context;
	}
	waitUntilGoalsReady() {
		return Promise.resolve();
	}
	waitUntilReady() {
		return Promise.resolve();
	}
	waitForInitialization() {
		return Promise.resolve();
	}
	identify(context, _hash, onDone) {
		this.context = context;
		if (onDone) onDone(null, this.flags);
		return Promise.resolve(this.flags);
	}
	getContext() {
		return this.context;
	}
	flush(onDone) {
		onDone?.();
		return Promise.resolve();
	}
	variation(key, defaultValue) {
		return this.flags[key] || defaultValue;
	}
	variationDetail(key, defaultValue) {
		return {
			value: this.flags[key] || defaultValue,
			variationIndex: 0,
			reason: { kind: "FALLTHROUGH" }
		};
	}
	setStreaming() {}
	on() {}
	off() {}
	track() {}
	addHook() {}
	allFlags() {
		return this.flags;
	}
	close(onDone) {
		onDone?.();
		return Promise.resolve();
	}
};
//#endregion
//#region src/FeatureFlags/internal/FakeLaunchDarkly/ContextProvider.tsx
var LdProvider = defaultReactOptions.reactContext.Provider;
var FakeLaunchDarklyFlagContextProvider = (props) => {
	const { flags = {}, user = { key: "fake-ld-flag-provider-user" }, children } = props;
	return /* @__PURE__ */ jsx(LdProvider, {
		value: {
			flags,
			flagKeyMap: {},
			ldClient: new FakeLaunchDarklyClient(flags, user)
		},
		children
	});
};
//#endregion
//#region src/FeatureFlags/internal/cookies.ts
var GLOBAL_COOKIES = new Cookies();
var SIGNED_IN_LD_USER_KEY_COOKIE_NAME = "ld_user_key";
/**
* getLaunchDarklyUserKeyCookieValue is here for the rest of the lib/flags package
* and it's unlikely you want to use it.
*
* getLaunchDarklyUserKeyCookieValue returns the value of the cookie our backend
* sets to identify the current user to LaunchDarkly.
*/
function getLaunchDarklyUserKeyCookieValue() {
	return GLOBAL_COOKIES.get(SIGNED_IN_LD_USER_KEY_COOKIE_NAME, { doNotParse: true });
}
//#endregion
//#region src/FeatureFlags/internal/FakeLaunchDarkly/Hoc.tsx
async function FakeLaunchDarklyProviderHoc() {
	const clientSideFlags = await fetch(process.env.REACT_APP_CLIENT_SIDE_FLAGS_API_FOR_LOCAL_DEV ?? "/api/v1/testing/client_side_flags").then((response) => {
		if (!response.ok) throw new Error(`Non-200 error code returned when fetching /api/v1/testing/client_side_flags for LaunchDarkly local dev set up. Was ${response.status}`);
		const contentType = response.headers.get("content-type");
		if (!contentType?.includes("application/json")) throw new Error(`Non-JSON response returned when fetching /api/v1/testing/client_side_flags for LaunchDarkly local dev set up. Has Content-Type '${contentType}'`);
		return response.json();
	});
	const userKey = getLaunchDarklyUserKeyCookieValue();
	const user = userKey ? { key: userKey } : { key: "missing-ld-user-key-but-maybe-okay-for-now" };
	const flags = clientSideFlags.flag_data;
	const FakeLdFlagContextProvider = ({ children }) => /* @__PURE__ */ jsx(FakeLaunchDarklyFlagContextProvider, {
		flags,
		user,
		children
	});
	return Promise.resolve(FakeLdFlagContextProvider);
}
//#endregion
//#region src/FeatureFlags/internal/RealLaunchDarkly/Hoc.tsx
var logToConsole = (message) => loggerUtils.logMessageToConsole(`[Launch Darkly] ${message}`);
var ldLogger = {
	debug: () => null,
	info: logToConsole,
	warn: logToConsole,
	error: (message) => {
		if (message && message.includes("network error")) logToConsole(message);
		else loggerUtils.logException(/* @__PURE__ */ new Error(`[Launch Darkly] ${message}`));
	}
};
/**
* RealLaunchDarklyProviderHoc is a higher order component, that asynchronously
* sets up the LaunchDarkly client and retrieves the flags before. Once
* resolved, this returns a Provider component.
*/
async function RealLaunchDarklyProviderHoc(clientId) {
	const userKey = getLaunchDarklyUserKeyCookieValue();
	return asyncWithLDProvider({
		user: userKey ? {
			kind: "user",
			key: userKey
		} : { anonymous: true },
		clientSideID: clientId,
		reactOptions: { useCamelCaseFlagKeys: false },
		options: { logger: ldLogger }
	});
}
//#endregion
//#region src/FeatureFlags/FeatureFlagProvider.tsx
/**
* FeatureFlagProviderHoc configures a LaunchDarkly provider with the correct
* client ID and initial LDUser object from the cookie state of the first
* request to handle both signed-in and anonymous users. To do so, it needs the
* flag state from a remote service (LaunchDarkly's in production, our local
* backend during development), and so returns a promise for the actual Provider
* object.
*
* It provides the @{link
* https://docs.launchdarkly.com/sdk/client-side/react/react-web#hooks |
* `useFlags` hook} and that's likely to handle your use case.
*
* See the API docs of
* {@link https://docs.launchdarkly.com/sdk/client-side/react/react-web | launchdarkly-react-client-sdk }
* for details on other hooks available.
*
* Informing LaunchDarkly of users' transitions from anonymous to signed-in and
* back are handled in newAuthState using {@link useLaunchDarklyUpdateUser}.
*
* In test and local development environments, it creates a fake LaunchDarkly
* provider that doesn't connect to the LaunchDarkly service, but does use an
* HTTP API on our backend to coordinate flag values with same said backend.
*/
async function FeatureFlagProviderHoc() {
	const env = getEnvironment();
	switch (env) {
		case Environment.PRODUCTION:
		case Environment.STAGING:
		case Environment.EPHEMERAL:
		case Environment.TESTING: return RealLaunchDarklyProviderHoc(getLaunchDarklyClientId(env));
		case Environment.LOCAL:
		case Environment.UNKNOWN:
		default:
			if (process.env.REACT_APP_LAUNCHDARKLY_FORCE_LOCAL_REQUESTS_TO_CLIENT_ID != null) return RealLaunchDarklyProviderHoc(process.env.REACT_APP_LAUNCHDARKLY_FORCE_LOCAL_REQUESTS_TO_CLIENT_ID);
			return FakeLaunchDarklyProviderHoc();
	}
}
//#endregion
//#region src/FeatureFlags/useLaunchDarklyUpdater.ts
/**
* Color mostly only relies on user context and not multi context.
* If we do ever expect to launch flags in non users contexts (like organization or populations)
* This method will need to be updated accordingly.
*/
function getUserKeyFromLdContext(context) {
	if ("kind" in context && context.kind === "multi") {
		const userContext = context.user;
		if (userContext !== "multi" && typeof userContext === "object") return userContext.key;
		return;
	}
	const { key } = context;
	return typeof key === "string" ? key : void 0;
}
/**
* useLaunchDarklyUpdater is likely not what you want to be using. It's meant to
* be used within the guts of newAuthState.
*
* useLaunchDarklyUpdater handles tracking users as they move from anonymous to
* signed-in and back in the the LaunchDarkly client. Calls to
* {@link ILaunchDarklyUpdater.update} method on the returned object will check
* for changes to the cookies returned by the server and adjust the local
* identification of the user. No remote services are called for that.
*/
var useLaunchDarklyUpdater = () => {
	const ldClient = useLDClient();
	if (!ldClient) throw new Error("useLaunchDarklyUpdateUser must be used within a ConfiguredFeatureFlagsProvider context");
	const updateLaunchDarkly = async () => {
		const newLDUserKey = getLaunchDarklyUserKeyCookieValue();
		if (newLDUserKey === getUserKeyFromLdContext(ldClient.getContext())) return;
		const user = newLDUserKey ? { key: newLDUserKey } : { anonymous: true };
		try {
			await ldClient.identify(user);
		} catch (error) {
			Sentry.withScope((scope) => {
				scope.setTag("subsystem", "feature-flags");
				loggerUtils.logException(error);
			});
		}
	};
	return { updateLaunchDarkly };
};
//#endregion
export { FeatureFlagProviderHoc as n, FakeLaunchDarklyFlagContextProvider as r, useLaunchDarklyUpdater as t };

//# sourceMappingURL=FeatureFlags-BYApvmNn.js.map